import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { calendarWeekdayLabels, danishCalendarMonthGrid } from '@/components/ui/danishCalendarGrid';
import { civilFromParts, formatClinicMonthTitle } from '@/lib/questionnaireDateFormat';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';

/**
 * Month grid shared with the questionnaire deadline picker.
 * `language` chooses month and weekday names (`da` or `en`). The week starts on Monday.
 */
export function DanishCalendarMonth({
  year,
  monthIndex,
  language,
  value,
  marked,
  min,
  onShift,
  onSelect,
}: {
  year: number;
  monthIndex: number;
  language: string;
  value: string;
  marked?: ReadonlySet<string>;
  min?: string;
  onShift: (delta: number) => void;
  onSelect: (civil: string) => void;
}) {
  const { t } = useTranslation();
  const weekdayLabels = calendarWeekdayLabels(language);

  return (
    <>
      <div className="ch">
        <span data-testid="danish-calendar-month">{formatClinicMonthTitle(year, monthIndex, language)}</span>
        <span className="nav2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('forms.picker.prev_month', 'Forrige måned')}
            onClick={() => onShift(-1)}
          >
            <ChevronLeft />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('forms.picker.next_month', 'Næste måned')}
            onClick={() => onShift(1)}
          >
            <ChevronRight />
          </Button>
        </span>
      </div>
      <table>
        <thead>
          <tr>
            {weekdayLabels.map((day) => (
              <th key={day}>{day}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {danishCalendarMonthGrid(year, monthIndex).map((row) => (
            <tr key={row.week}>
              <td className="wk">
                <span>{row.week}</span>
              </td>
              {row.days.map((day, index) => {
                if (day == null) return <td key={`e-${index}`} />;
                const civil = civilFromParts(year, monthIndex, day);
                const selected = civil === value;
                const appointment = marked?.has(civil) ?? false;
                const past = Boolean(min && civil < min);
                return (
                  <td key={civil} className={cn(selected && 'sel', appointment && 'ap', past && 'past')}>
                    <button
                      type="button"
                      disabled={past}
                      aria-current={selected ? 'date' : undefined}
                      onClick={() => onSelect(civil)}
                    >
                      <span>{day}</span>
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
